/**
 * CWS-1908: the prepaid redemption code an attendant types when the QR cannot be scanned.
 *
 * Mirrors svc-order `utils/prepaidRedemptionCode`: eight hex characters shown as XXXX-XXXX.
 * Hex never contains O, I or L, so those are the only misreadings possible and are mapped
 * back to the digits they stand for. The server does the same, so what is sent is only a
 * courtesy; the field can still enable Find as soon as the code is complete.
 */

export const REDEMPTION_CODE_HEX_LENGTH = 8;

/** The eight hex characters a typed code names, or null until it is complete and valid. */
export function normalizeRedemptionCodeInput(raw: string | null | undefined): string | null {
  const cleaned = String(raw ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
  return /^[0-9A-F]{8}$/.test(cleaned) ? cleaned : null;
}

/** "3F9A2B7C" as "3F9A-2B7C". */
export function formatRedemptionCode(hex: string): string {
  const upper = String(hex).toUpperCase();
  return `${upper.slice(0, 4)}-${upper.slice(4, REDEMPTION_CODE_HEX_LENGTH)}`;
}

/** What to show in the field as the attendant types: grouped, uppercased, never over-long. */
export function formatRedemptionCodeWhileTyping(raw: string | null | undefined): string {
  const cleaned = String(raw ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, REDEMPTION_CODE_HEX_LENGTH);
  return cleaned.length > 4 ? `${cleaned.slice(0, 4)}-${cleaned.slice(4)}` : cleaned;
}

/**
 * Whether a failed prepaid QR lookup is worth offering the typed code for.
 *
 * A refusal is: the book is unknown, cancelled, expired or another company's, and the code
 * names a different book the attendant can still reach. A network drop or a server fault is
 * not: the code goes to the same service and would fail the same way, so offering it sends
 * the attendant round a loop that cannot succeed. Those get the plain error instead.
 *
 * An error with no HTTP status is a refusal the API returned in its body (status false, or a
 * payload we could not read), which is about this QR and so does offer the code.
 */
export function shouldOfferCodeAfterQrFailure(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== 'number') {
    return true;
  }
  return status >= 400 && status < 500;
}
